import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, ScaleIcon, PrinterIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, ClipboardListIcon, ChevronRightIcon, PackageIcon } from 'lucide-react';
interface WeighingModuleProps {
  materialId: string;
  orderId: string;
  onBack: () => void;
  onComplete: () => void;
}
interface Material {
  id: string;
  name: string;
  orderId: string;
  orderName: string;
  targetWeight: number;
  tolerance: number;
  unit: string;
  lotId: string;
  lotSize: number; // Anzahl der Boxen im Lot
  lotTotalWeight: number; // Gesamtgewicht des Lots
  boxesWeighed: number; // Bereits gewogene Boxen im Lot
  boxesRemaining: number; // Noch zu wiegende Boxen im Lot
  lotWeighedSoFar: number; // Bisher gewogenes Gewicht im Lot
}
interface PendingWeighing {
  id: string;
  materialId: string;
  materialName: string;
  orderId: string;
  orderName: string;
  targetWeight: number;
  unit: string;
  lotId: string;
  lotSize: number;
  boxesWeighed: number;
}
export function WeighingModule({
  materialId,
  orderId,
  onBack,
  onComplete
}: WeighingModuleProps) {
  const [weight, setWeight] = useState<number | null>(null);
  const [isWeighing, setIsWeighing] = useState(false);
  const [isWithinTolerance, setIsWithinTolerance] = useState<boolean | null>(null);
  const [isPrinted, setIsPrinted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [cumulativeWeight, setCumulativeWeight] = useState(0);
  const [pendingWeighings, setPendingWeighings] = useState<PendingWeighing[]>([{
    id: 'W003',
    materialId: 'R161215',
    materialName: 'Langkornreis',
    orderId: '1001',
    orderName: 'Gustoria Paella',
    targetWeight: 30.0,
    unit: 'kg',
    lotId: 'L2023-112',
    lotSize: 5,
    boxesWeighed: 2
  }, {
    id: 'W004',
    materialId: 'R090533',
    materialName: 'Erbsen',
    orderId: '1001',
    orderName: 'Gustoria Paella',
    targetWeight: 10.2,
    unit: 'kg',
    lotId: 'L2023-113',
    lotSize: 3,
    boxesWeighed: 0
  }]);
  // Beispiel-Material zum Verwiegen mit Lot-Informationen
  const material: Material = {
    id: materialId,
    name: materialId === 'R061307' ? 'MSC Alaska Seelachs' : materialId === 'R053408' ? 'Hähnchen Brust St gebraten' : materialId === 'R161215' ? 'Langkornreis' : 'Erbsen',
    orderId: orderId,
    orderName: 'Gustoria Paella',
    targetWeight: materialId === 'R061307' ? 200.0 : materialId === 'R053408' ? 200.0 : materialId === 'R161215' ? 150.0 : 100.0,
    tolerance: materialId === 'R061307' ? 10.0 : materialId === 'R053408' ? 10.0 : materialId === 'R161215' ? 7.5 : 5.0,
    unit: 'kg',
    lotId: materialId === 'R061307' ? 'L2023-110' : materialId === 'R053408' ? 'L2023-111' : materialId === 'R161215' ? 'L2023-112' : 'L2023-113',
    lotSize: materialId === 'R061307' ? 5 : materialId === 'R053408' ? 5 : materialId === 'R161215' ? 5 : 3,
    lotTotalWeight: materialId === 'R061307' ? 1000 : materialId === 'R053408' ? 1000 : materialId === 'R161215' ? 750 : 300,
    boxesWeighed: materialId === 'R061307' ? 3 : materialId === 'R053408' ? 2 : materialId === 'R161215' ? 2 : 1,
    boxesRemaining: materialId === 'R061307' ? 2 : materialId === 'R053408' ? 3 : materialId === 'R161215' ? 3 : 2,
    lotWeighedSoFar: materialId === 'R061307' ? 604.5 : materialId === 'R053408' ? 401.2 : materialId === 'R161215' ? 302.7 : 98.5
  };
  const startWeighing = () => {
    setIsWeighing(true);
    // Simuliere den Wiegeprozess
    setTimeout(() => {
      // Zufälliges Gewicht innerhalb oder außerhalb der Toleranz
      const randomWeight = material.targetWeight + (Math.random() * (material.tolerance * 1.2) - material.tolerance * 0.6);
      setWeight(parseFloat(randomWeight.toFixed(2)));
      setIsWeighing(false);
      // Aktualisiere das kumulative Gewicht
      const newCumulativeWeight = cumulativeWeight + randomWeight;
      setCumulativeWeight(newCumulativeWeight);
      // Prüfe, ob innerhalb der Toleranz für das gesamte Lot
      const totalAfterThisBox = material.lotWeighedSoFar + randomWeight;
      const totalTarget = material.lotTotalWeight;
      // Die Toleranz bezieht sich auf das gesamte Lot
      const minAllowed = totalTarget - material.tolerance;
      const maxAllowed = totalTarget + material.tolerance;
      // Prüfe, ob die Gesamtmenge nach dieser Box innerhalb der Toleranz liegt
      // Bei der letzten Box muss das Gesamtgewicht innerhalb der Toleranz liegen
      // Bei früheren Boxen prüfen wir, ob es noch möglich ist, innerhalb der Toleranz zu landen
      const isLastBox = material.boxesRemaining === 1;
      if (isLastBox) {
        // Bei der letzten Box muss das Gesamtgewicht innerhalb der Toleranz liegen
        setIsWithinTolerance(totalAfterThisBox >= minAllowed && totalAfterThisBox <= maxAllowed);
      } else {
        // Bei früheren Boxen: Prüfen, ob wir noch innerhalb der Toleranz landen können
        const remainingBoxesMinWeight = (material.boxesRemaining - 1) * (material.targetWeight - material.tolerance / material.lotSize);
        const remainingBoxesMaxWeight = (material.boxesRemaining - 1) * (material.targetWeight + material.tolerance / material.lotSize);
        const canStillReachMin = totalAfterThisBox + remainingBoxesMinWeight >= minAllowed;
        const canStillReachMax = totalAfterThisBox + remainingBoxesMaxWeight <= maxAllowed;
        setIsWithinTolerance(canStillReachMin && canStillReachMax);
      }
    }, 2000);
  };
  const printLabel = () => {
    // Simuliere Druckvorgang
    setIsPrinted(true);
    // Zeige Erfolgsmeldung nach dem Druck
    setTimeout(() => {
      setShowSuccess(true);
      // Nach kurzer Zeit zurück zum nächsten Material
      setTimeout(() => {
        onComplete();
      }, 2000);
    }, 1000);
  };
  const getWeightStatus = () => {
    if (isWithinTolerance === null) return '';
    return isWithinTolerance ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
  };
  // Berechne den Fortschritt der Gesamtverwiegung des Lots
  const lotWeightProgress = (material.lotWeighedSoFar + (weight || 0)) / material.lotTotalWeight * 100;
  return <div className="container mx-auto" data-id="element-2567">
      <div className="mb-4" data-id="element-2568">
        <div className="flex items-center justify-between" data-id="element-2569">
          <div data-id="element-2570">
            <h2 className="text-xl font-bold text-gray-800 mb-1" data-id="element-2571">Verwiegen</h2>
            <p className="text-sm text-gray-600" data-id="element-2572">
              Auftrag #{material.orderId}: {material.orderName}
            </p>
          </div>
          <button onClick={onBack} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-gray-700 hover:bg-gray-100" data-id="element-2573">
            <ArrowLeftIcon size={16} data-id="element-2574" />
            <span data-id="element-2575">Zurück</span>
          </button>
        </div>
      </div>
      {showSuccess ? <div className="bg-white rounded-xl shadow-md p-8 text-center" data-id="element-2576">
          <div className="bg-green-100 p-3 rounded-full inline-flex mb-4" data-id="element-2577">
            <CheckCircleIcon size={48} className="text-green-600" data-id="element-2578" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-2579">
            Verwiegung erfolgreich!
          </h3>
          <p className="text-gray-600 mb-2" data-id="element-2580">
            Etikett wurde gedruckt und Material erfolgreich verarbeitet.
          </p>
          <p className="text-sm text-gray-500" data-id="element-2581">
            Sie werden automatisch weitergeleitet...
          </p>
        </div> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-2582">
          <div className="lg:col-span-2" data-id="element-2583">
            <div className="bg-white rounded-xl shadow-md p-5" data-id="element-2584">
              {/* Kompakte Material-Info mit Tabs oder Accordion */}
              <div className="flex items-center justify-between mb-4" data-id="element-2585">
                <h3 className="text-lg font-semibold text-gray-800" data-id="element-2586">
                  {material.name}{' '}
                  <span className="text-sm font-normal text-gray-500" data-id="element-2587">
                    ({material.id})
                  </span>
                </h3>
                <div className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full" data-id="element-2588">
                  Box {material.lotSize - material.boxesRemaining + 1}/
                  {material.lotSize}
                </div>
              </div>
              {/* Lot-Fortschritt - kompakter */}
              <div className="mb-4 bg-gray-50 p-3 rounded-lg" data-id="element-2589">
                <div className="flex justify-between items-center mb-1 text-sm" data-id="element-2590">
                  <span className="font-medium text-gray-700" data-id="element-2591">
                    Lot-Fortschritt:
                  </span>
                  <span className="font-medium text-gray-700" data-id="element-2592">
                    {(material.lotWeighedSoFar + (weight || 0)).toFixed(1)}/
                    {material.lotTotalWeight} {material.unit}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-1" data-id="element-2593">
                  <div className="bg-[#8DC63F] h-2 rounded-full transition-all duration-500" style={{
                width: `${Math.min(lotWeightProgress, 100)}%`
              }} data-id="element-2594"></div>
                </div>
              </div>
              {/* Vereinfachter Wiegebereich */}
              <div className={`border rounded-lg p-5 mb-4 ${getWeightStatus()}`} data-id="element-2595">
                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-2596">
                  {/* Wiegeanzeige */}
                  <div className="flex-shrink-0 text-center" data-id="element-2597">
                    <div className={`
                      p-4 rounded-lg inline-block 
                      ${!weight ? 'bg-gray-100' : isWithinTolerance ? 'bg-green-100' : 'bg-red-100'}
                    `} data-id="element-2598">
                      {!weight ? <ScaleIcon size={40} className="text-gray-500 mx-auto" data-id="element-2599" /> : isWithinTolerance ? <CheckCircleIcon size={40} className="text-green-600 mx-auto" data-id="element-2600" /> : <XCircleIcon size={40} className="text-red-600 mx-auto" data-id="element-2601" />}
                      <div className="text-2xl font-bold mt-2" data-id="element-2602">
                        {weight ? weight : '--'} {material.unit}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-2603">
                        Ziel: {material.targetWeight} ±{material.tolerance}{' '}
                        {material.unit}
                      </div>
                    </div>
                  </div>
                  {/* Anweisungen und Aktionen */}
                  <div className="flex-1" data-id="element-2604">
                    {!weight ? <div data-id="element-2605">
                        <p className="text-sm text-gray-600 mb-3" data-id="element-2606">
                          Platzieren Sie die Kiste auf der Waage und starten Sie
                          den Wiegevorgang.
                        </p>
                        <button onClick={startWeighing} disabled={isWeighing} className={`
                          bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg 
                          text-sm font-medium transition-colors flex items-center gap-2
                          ${isWeighing ? 'opacity-70 cursor-not-allowed' : ''}
                        `} data-id="element-2607">
                          <ScaleIcon size={16} data-id="element-2608" />
                          <span data-id="element-2609">
                            {isWeighing ? 'Wiegen...' : 'Wiegen starten'}
                          </span>
                        </button>
                      </div> : isWithinTolerance ? <div data-id="element-2610">
                        <p className="text-sm text-green-600 mb-3" data-id="element-2611">
                          {material.boxesRemaining === 1 ? 'Das Lot wurde vollständig innerhalb der Toleranz verwogen.' : 'Die Box wurde erfolgreich verwogen.'}
                        </p>
                        <button onClick={printLabel} disabled={isPrinted} className={`
                          bg-[#8DC63F] hover:bg-[#7AB929] text-white px-4 py-2 rounded-lg 
                          text-sm font-medium transition-colors flex items-center gap-2
                          ${isPrinted ? 'opacity-70 cursor-not-allowed' : ''}
                        `} data-id="element-2612">
                          <PrinterIcon size={16} data-id="element-2613" />
                          <span data-id="element-2614">
                            {isPrinted ? 'Drucken...' : 'Etikett drucken'}
                          </span>
                        </button>
                      </div> : <div data-id="element-2615">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3" data-id="element-2616">
                          <p className="text-sm text-red-600 flex items-start" data-id="element-2617">
                            <AlertTriangleIcon size={16} className="mr-1 flex-shrink-0 mt-0.5" data-id="element-2618" />
                            <span data-id="element-2619">
                              Gewicht außerhalb der Toleranz! Bitte korrigieren
                              Sie die Menge und wiegen Sie erneut.
                            </span>
                          </p>
                        </div>
                        <button onClick={startWeighing} disabled={isWeighing} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-2620">
                          <ScaleIcon size={16} data-id="element-2621" />
                          <span data-id="element-2622">Erneut wiegen</span>
                        </button>
                      </div>}
                  </div>
                </div>
              </div>
              {/* Kompakte Etikett-Vorschau */}
              {weight && isWithinTolerance && <div className="border border-gray-200 p-3 rounded-lg" data-id="element-2623">
                  <div className="flex items-center justify-between mb-2" data-id="element-2624">
                    <h4 className="text-sm font-medium text-gray-700" data-id="element-2625">
                      Etikett-Vorschau
                    </h4>
                    <span className="text-xs text-gray-500" data-id="element-2626">
                      Wird nach Bestätigung gedruckt
                    </span>
                  </div>
                  <div className="border border-gray-200 p-3 rounded-md bg-white text-sm" data-id="element-2627">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1" data-id="element-2628">
                      <div className="text-gray-600" data-id="element-2629">Material:</div>
                      <div className="font-medium" data-id="element-2630">{material.name}</div>
                      <div className="text-gray-600" data-id="element-2631">Artikel-Nr:</div>
                      <div className="font-medium" data-id="element-2632">{material.id}</div>
                      <div className="text-gray-600" data-id="element-2633">Box:</div>
                      <div className="font-medium" data-id="element-2634">
                        {material.lotSize - material.boxesRemaining + 1}/
                        {material.lotSize}
                      </div>
                      <div className="text-gray-600" data-id="element-2635">Gewicht:</div>
                      <div className="font-medium" data-id="element-2636">
                        {weight} {material.unit}
                      </div>
                      <div className="text-gray-600" data-id="element-2637">Datum/Zeit:</div>
                      <div className="font-medium" data-id="element-2638">
                        {new Date().toLocaleDateString()}{' '}
                        {new Date().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
          {/* Vereinfachte Seitenleiste */}
          <div className="lg:col-span-1" data-id="element-2639">
            <div className="bg-white rounded-xl shadow-md p-4" data-id="element-2640">
              {/* Lot-Informationen - kompakter */}
              <div className="mb-4" data-id="element-2641">
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center" data-id="element-2642">
                  <PackageIcon size={16} className="mr-1" data-id="element-2643" />
                  <span data-id="element-2644">Lot: {material.lotId}</span>
                </h4>
                <div className="bg-blue-50 p-3 rounded-lg text-sm" data-id="element-2645">
                  <div className="grid grid-cols-2 gap-1" data-id="element-2646">
                    <div className="text-gray-600" data-id="element-2647">Gesamtgewicht:</div>
                    <div className="font-medium text-right" data-id="element-2648">
                      {material.lotTotalWeight} {material.unit}
                    </div>
                    <div className="text-gray-600" data-id="element-2649">Bisher verwogen:</div>
                    <div className="font-medium text-right" data-id="element-2650">
                      {material.lotWeighedSoFar} {material.unit}
                    </div>
                    <div className="text-gray-600" data-id="element-2651">Boxen:</div>
                    <div className="font-medium text-right" data-id="element-2652">
                      {material.boxesWeighed}/{material.lotSize}
                    </div>
                  </div>
                  <div className="mt-2" data-id="element-2653">
                    <div className="w-full bg-blue-200 rounded-full h-1.5 mb-1" data-id="element-2654">
                      <div className="bg-blue-600 h-1.5 rounded-full" style={{
                    width: `${material.boxesWeighed / material.lotSize * 100}%`
                  }} data-id="element-2655"></div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Ausstehende Verwiegungen - kompakter */}
              {pendingWeighings.length > 0 && <div className="mb-4" data-id="element-2656">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center" data-id="element-2657">
                    <ClipboardListIcon size={16} className="mr-1" data-id="element-2658" />
                    <span data-id="element-2659">Ausstehende Verwiegungen</span>
                  </h4>
                  <div className="space-y-2" data-id="element-2660">
                    {pendingWeighings.map(pending => <div key={pending.id} className="border border-gray-200 rounded-lg p-2 text-sm" data-id="element-2661">
                        <div className="font-medium" data-id="element-2662">
                          {pending.materialName}
                        </div>
                        <div className="flex justify-between items-center mt-1" data-id="element-2663">
                          <span className="text-gray-600" data-id="element-2664">
                            {pending.targetWeight} {pending.unit}
                          </span>
                          <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full" data-id="element-2665">
                            {pending.boxesWeighed + 1}/{pending.lotSize}
                          </span>
                        </div>
                      </div>)}
                  </div>
                </div>}
              {/* Hinweis - kompakter */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-id="element-2666">
                <p className="text-xs text-yellow-700 flex items-start" data-id="element-2667">
                  <AlertTriangleIcon size={14} className="mr-1 flex-shrink-0 mt-0.5" data-id="element-2668" />
                  <span data-id="element-2669">
                    Toleranz bezieht sich auf das Gesamtgewicht des Lots (±
                    {material.tolerance} {material.unit}).
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}